/* eslint-disable no-use-before-define */
// import clevertap from "clevertap-web-sdk";

import isMobile, { getBrowserName, getOperatingSystem, removeEmptyNullAndUndefinedFromObject } from "../utils/common";
import { campaignEvents, cleverTapCommonEvents } from "./clevertapConstants";
import CookieUtil from "../utils/storage/cookieUtil";
import { fetchGeoLocation } from "../api/common";
import { getPageNameForCleverTap } from "../utils/fnpPageType";
import { execDataLayer } from "../utils/cache-layer/cacheLayer";
import { getLocalStorageItem, setLocalStorageItem } from "../utils/storage/localStorageUtil";
import { shallowEqual, useSelector } from "react-redux";

let clevertap;

/**
 *
 * @returns
 */
const useCleverTap = () => {
  const cookieTTL = 20 * 60; // 20 minutes
  const options = {
    maxAge: cookieTTL,
    path: "/",
  };
  const LAST_ACTIVITY_KEY = "cleverTapLastActivityExpiresAt";
  const { disabledEvents } = useSelector(
    (state) => ({
      disabledEvents: Array.isArray(state?.ctDisabledEvents?.eventsList) ? state.ctDisabledEvents.eventsList : [],
    }),
    shallowEqual,
  );

  /**
   *
   */
  const init = () => {
    try {
      if (typeof window !== "undefined") {
        clevertap = require("clevertap-web-sdk"); // eslint-disable-line global-require
        clevertap.init(process.env.NEXT_PUBLIC_CLEVER_TAP_ACCOUNT_ID, process.env.NEXT_PUBLIC_CLEVER_TAP_REGION);
        clevertap.privacy.push({ useIP: true });
      }
      setTimeout(() => {
        const cleverTapID = clevertap?.getCleverTapID();
        const checkLastInActive = checkLastActivityTime(Date.now());
        if (cleverTapID) {
          customWebSession(
            { page_name: getPageNameForCleverTap(window?.fnpPageType), page_type: window.fnpPageType },
            checkLastInActive,
            cleverTapID,
          );
        }
      }, 500);
      // clevertap.setDebugLevel(3); // Debug level can be adjusted based on needs
      console.log("CleverTap Initialized");
    } catch (error) {
      console.log("Clevertap not initialized properly");
    }
  };

  /**
   * To get the unique session id.
   * @returns
   */
  const getSessionID = () => {
    const timeStamp = Date.now();
    const generatedSessionID = `${isMobile() ? "m" : "d"}_${timeStamp}_${Math.floor(Math.random() * 100)}`;
    return generatedSessionID;
  };

  /**
   * To track user activity
   */
  const updateLastActivityTime = (forceUpdate) => {
    const isInactiveStatus = checkLastActivityTime(Date.now());
    if (!isInactiveStatus || forceUpdate) {
      const newExpiry = Date.now() + cookieTTL * 1000;
      setLocalStorageItem(LAST_ACTIVITY_KEY, newExpiry.toString());
      const cleverTapUserSessionID = CookieUtil.getCookie("cleverTapUserSessionID");
      const fetchSessionID = cleverTapUserSessionID || getSessionID();
      CookieUtil.setCookie("cleverTapUserSessionID", fetchSessionID, options);
      CookieUtil.setCookie("cleverTapWebSession", "webSessionStarted", options);
    }
  };

  /**
   * To check the last activity time of user.
   * If the user is inactive for 20 minutes, it will trigger a custom web session event.
   */
  const checkLastActivityTime = (now) => {
    const lastActivityExpiresAt = parseInt(getLocalStorageItem(LAST_ACTIVITY_KEY), 10);
    if (!lastActivityExpiresAt) {
      return true;
    }
    const isInactive = now > lastActivityExpiresAt;
    return isInactive;
  };

  /**
   * It will add analytics_session_id in ctap data.
   * @param {*} cTapData
   * @param {*} checkLastInActive
   * @returns
   */
  const updateSessionIDEventProperties = (cTapData, checkLastInActive) => {
    const cleverTapUserSessionID = CookieUtil.getCookie("cleverTapUserSessionID");
    const fetchSessionID = cleverTapUserSessionID || getSessionID();
    if (!cleverTapUserSessionID && checkLastInActive) {
      CookieUtil.setCookie("cleverTapUserSessionID", fetchSessionID, options);
    }
    cTapData.analytics_sessionid = fetchSessionID;
    return cTapData;
  };

  /**
   *
   * @param {*} eventName
   * @param {*} properties
   */
  const trackEventCleverTap = async (eventName, properties = {}) => {
    try {
      if (Object.values(campaignEvents).includes(eventName)) {
        clevertap?.[eventName](properties);
      } else {
        const checkLastInActive = checkLastActivityTime(Date.now());
        await customWebSession(properties, checkLastInActive);
        const cleverTapEventData = updateSessionIDEventProperties(properties, checkLastInActive);
        try {
          const geoJSON = await execDataLayer(fetchGeoLocation, "geojs");
          cleverTapEventData.browsing_city = geoJSON?.city;
        } catch (geoError) {
          console.log("Failed to fetch geo location");
        }
        !checkLastInActive && updateLastActivityTime(true);
        !disabledEvents.includes(eventName) &&
          clevertap.event.push(eventName, removeEmptyNullAndUndefinedFromObject(cleverTapEventData));
      }
    } catch (error) {
      console.log("Clevertap not initialized properly");
    }
  };
  /**
   * It will get triggered only one time of user inactivity of 20 minutes.
   */
  const customWebSession = async (properties, checkLastInActive, cleverTapID = "") => {
    const cleverTapWebSession = CookieUtil.getCookie("cleverTapWebSession");

    const searchQuery = Object.fromEntries(new URLSearchParams(window.location.search));
    const { utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign, gclid } = searchQuery;

    if (!cleverTapWebSession && checkLastInActive) {
      const cTapId = cleverTapID || properties?.ctid || window?.clevertap?.getCleverTapID();
      const referrer = document?.referrer || "";
      const payload = {
        browser: getBrowserName(),
        page_name: properties?.page_name,
        page_type: properties?.page_type,
        complete_url: window.location.href,
        clean_url: `${window.location.origin}${window.location.pathname}`,
        operating_system: getOperatingSystem(),
        utm_source_name: utmSource,
        utm_medium_name: utmMedium,
        utm_campaign_name: utmCampaign,
        login_status: properties?.login_status,
        ctid: cTapId,
        platform: properties?.platform || (isMobile() ? "mobile" : "desktop"),
        webVersion: "v1",
        referrer,
        gclid,
      };

      CookieUtil.setCookie("cleverTapWebSession", "webSessionStarted", options);

      const cleverTapEventData = updateSessionIDEventProperties(payload, checkLastInActive);
      !disabledEvents.includes(cleverTapCommonEvents.sessionStart) &&
        clevertap.event.push(cleverTapCommonEvents.sessionStart, cleverTapEventData);

      updateLastActivityTime(true);
    }
  };

  /**
   *
   * @param {*} properties
   */
  const onUserLogin = (properties) => {
    try {
      clevertap.onUserLogin.push(properties);
    } catch (error) {
      console.log("Clevertap not initialized properly");
    }
  };

  return { init, trackEventCleverTap, onUserLogin };
};

export default useCleverTap;

/**
 * showCleverTapNotification this function handle notification for web-native campaign
 * Notification Popup will not get triggered in case of (iOS or MacOS) with Safari Browser.
 */
export const showCleverTapNotification = () => {
  try {
    if (
      typeof window !== "undefined" &&
      typeof clevertap !== "undefined" &&
      !((getOperatingSystem() === "iOS" || getOperatingSystem() === "MacOS") && getBrowserName() === "Safari") &&
      typeof clevertap.notifications !== "undefined" &&
      typeof clevertap.notifications.push === "function"
    ) {
      clevertap.notifications.push({
        titleText: "Stay Updated!",
        bodyText: "Allow notifications to receive the latest offers and updates.",
        okButtonText: "Allow",
        rejectButtonText: "No, Thanks",
        askAgainTimeInSeconds: 86400,
        okButtonColor: "#737530",
        serviceWorkerPath: "/clevertap_sw.js",
      });
    } else {
      console.warn("⚠️ CleverTap is not available or not initialized yet.");
    }
  } catch (error) {
    console.error("❌ Failed to show CleverTap notification:", error);
  }
};
